import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { duplicateConditionAction } from 'editor/src/store/design/slice';
import { ConditionAddress } from 'editor/src/store/design/types';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

import { conditionOptionUuids, conditionUuids } from 'editor/src/util/uuids';

/**
 * Duplicates a condition next to itself, in the same children bucket: same
 * name (suffixed), display settings and option labels/images, but nothing
 * LINKED to it — no elements, no sub-conditions, no text variants. The active
 * option carries over (the copy of the original's active option).
 *
 * Returns the new condition's id so the caller can open its settings once its
 * card has mounted (see pendingConditionToOpen).
 */
const duplicateConditionOperation =
  (address: ConditionAddress): Thunk =>
  (dispatch, getState, { i18n }) => {
    const source =
      getState().design.designData?.spreads[address.spreadIndex]?.conditionGroup?.conditions[address.conditionId];
    if (!source) {
      return undefined;
    }

    const newConditionId = conditionUuids.generate();
    const optionIdMap = source.options.map((option) => ({
      oldId: option.id,
      newId: conditionOptionUuids.generate(),
    }));
    const suffix = i18n.t('editor-condition-duplicate-suffix', ' (copy)');
    const newName = `${source.name}${suffix}`;

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Duplicate condition'));
      dispatch(duplicateConditionAction({ address, newConditionId, newName, optionIdMap }));
    });

    return newConditionId;
  };

export default duplicateConditionOperation;
